import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, Search as SearchIcon, Mail } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SiteShell } from "@/components/layout/SiteShell";
import { PlatformFeeNotice } from "@/components/marketplace/PlatformFeeNotice";

/**
 * Public FAQ page.
 *
 * - Accessible signed-in or signed-out (mounted outside <ProtectedRoute/>).
 * - Accordion layout: **one question expanded at a time** (single-collapsible).
 * - Content is verbatim from the founder-approved copy.
 * - All email addresses rendered as ``mailto:`` links.
 * - Uses SiteShell + design tokens so the styling matches Terms / Privacy /
 *   Community Guidelines pages: same eyebrow chip, same font-serif hero,
 *   same container widths and spacing.
 */

const CONTACT_EMAIL = "hello@lastbookmark.com";

// Small helper — renders paragraphs, mailto links, and simple lists that
// appear a couple of times in the copy. Kept inline so this file stays a
// single self-contained page and doesn't leak abstractions elsewhere.
function P({ children }) {
  return <p className="text-sm sm:text-base leading-relaxed text-foreground/90">{children}</p>;
}
function Email({ address = CONTACT_EMAIL }) {
  return (
    <a
      href={`mailto:${address}`}
      className="text-[hsl(var(--sb-clay))] underline underline-offset-4 hover:no-underline break-all"
      data-testid={`faq-mailto-${address}`}
    >
      {address}
    </a>
  );
}

const FAQ_ITEMS = [
  {
    id: "what-is",
    q: "What is Last Bookmark?",
    a: (
      <>
        <P>
          Last Bookmark is an online platform where authors and beta readers can connect for
          thoughtful feedback and creative collaboration. It provides a dedicated community built
          specifically for writers and readers to connect, communicate, and work together in a
          welcoming and respectful environment.
        </P>
      </>
    ),
  },
  {
    id: "who-can-join",
    q: "Who can join Last Bookmark?",
    a: <P>Anyone age 18 or older may join Last Bookmark as an author, a beta reader, or both.</P>,
  },
  {
    id: "in-beta",
    q: "Is Last Bookmark currently in beta?",
    a: (
      <>
        <P>
          Yes. Last Bookmark is currently in beta testing, which means the platform is open to real
          users while features are still being tested, refined, and improved.
        </P>
        <P>
          Users may occasionally encounter bugs, unfinished features, or changes as the platform
          continues to develop. Feedback from beta users helps shape the future of Last Bookmark.
        </P>
      </>
    ),
  },
  {
    id: "real-profiles",
    q: "Are the profiles and projects real?",
    a: (
      <>
        <P>
          Last Bookmark includes real registered users as well as some built-in demo profiles and
          example projects used to demonstrate how the platform works.
        </P>
        <P>
          All demo profiles and example projects should be clearly marked so users can easily
          distinguish them from real opportunities.
        </P>
      </>
    ),
  },
  {
    id: "author-post-project",
    q: "How do authors post a project?",
    a: (
      <>
        <P>
          Authors can post a project through their dashboard by providing information such as the
          project title, genre, synopsis, word count, deadline, budget, type of feedback needed,
          reading preferences, and any special requirements.
        </P>
        <P>Authors are responsible for making sure the information in their project listing is accurate.</P>
      </>
    ),
  },
  {
    id: "reader-apply",
    q: "How do beta readers apply for a project?",
    a: (
      <>
        <P>
          Beta readers can browse available projects and submit an application explaining their
          experience, availability, interests, and why they may be a good fit for the project.
        </P>
        <P>Submitting an application does not guarantee that the author will select that beta reader.</P>
      </>
    ),
  },
  {
    id: "coffee-chat",
    q: "What is a Coffee Chat?",
    a: (
      <>
        <P>
          A Coffee Chat is a private message conversation that lets authors and beta readers
          discuss a project before committing to work together. Use it to ask questions, clarify
          expectations, discuss availability, and see whether the project feels like a good fit.
          It is not a scheduled video or phone call.
        </P>
        <P>
          To start one, send a short introductory message. No appointment time is needed. If the
          other person accepts, a private chat opens automatically so you can continue the
          conversation in-platform.
        </P>
        <P>
          Coffee Chats are available before an application is accepted or a reader is hired,
          giving both people a low-pressure way to determine whether they're a good fit.
        </P>
      </>
    ),
  },
  {
    id: "message-immediately",
    q: "Can I message another user immediately?",
    a: (
      <>
        <P>
          Full messaging generally becomes available after an author accepts a beta reader’s
          application or approves a Coffee Chat request.
        </P>
        <P>
          This helps ensure that communication is mutual rather than allowing users to send
          unlimited unsolicited messages.
        </P>
        <P>
          All users must communicate respectfully and follow the{" "}
          <Link to="/community-guidelines" className="text-[hsl(var(--sb-clay))] underline underline-offset-4 hover:no-underline">
            Community Guidelines
          </Link>
          .
        </P>
      </>
    ),
  },
  {
    id: "payments-beta",
    q: "Does Last Bookmark currently process payments?",
    a: (
      <>
        <P>
          Not yet. While Last Bookmark is in beta, users may browse projects, submit applications,
          approve Coffee Chats, communicate, and complete work, but Last Bookmark does not currently
          process, hold, guarantee, or protect payments.
        </P>
        <P>
          Any payment arrangements made during beta are handled independently between the author and
          beta reader and are completed at their own risk.
        </P>
        <P>
          Users should clearly discuss the payment amount, deadline, work requirements, and
          expectations before beginning a paid project.
        </P>
      </>
    ),
  },
  {
    id: "payments-future",
    q: "How will payments work in the future?",
    a: (
      <>
        <P>
          Last Bookmark plans to introduce a secure in-platform payment system in a future phase.
        </P>
        <P>
          Once the payment system is available, payments for projects offered through Last Bookmark
          will be required to go through the platform rather than being arranged privately between
          users.
        </P>
        <P>
          Once the payment system is introduced, the exact payment flow, protections, fees, and
          dispute process will be clearly disclosed inside the app before those features become
          available.
        </P>
        <P>More information will be provided before the payment system officially launches.</P>
      </>
    ),
  },
  {
    id: "monthly-fee",
    q: "Will Last Bookmark charge a monthly fee?",
    a: (
      <>
        <P>
          A paid monthly membership will not be required simply to create an account and use the
          basic platform.
        </P>
        <P>
          Once in-platform payments are introduced, a small service fee may be deducted from
          completed paid projects. Any applicable fee will be clearly disclosed before a user agrees
          to a transaction.
        </P>
        <P>
          This service fee will help cover the cost of operating, maintaining, securing, and
          continuing to improve Last Bookmark so the platform can remain available to authors and
          beta readers.
        </P>
        <P>
          Optional premium membership features may also be introduced in the future, but users will
          be informed of the price and included benefits before choosing to subscribe.
        </P>
      </>
    ),
  },
  {
    id: "guarantee",
    q: "Does Last Bookmark guarantee payment or project completion?",
    a: (
      <>
        <P>
          During beta, Last Bookmark cannot guarantee payment because all payment arrangements are
          currently handled independently between users.
        </P>
        <P>
          Once the future payment system is introduced, the exact payment flow, protections, fees,
          and dispute process will be clearly disclosed inside the app before those features
          become available. Last Bookmark still cannot guarantee that every project will be
          completed, that every deadline will be met, or that either person will be satisfied with
          the final result.
        </P>
        <P>
          Authors and beta readers remain responsible for communicating clearly, choosing who they
          work with, and following the terms of their agreement.
        </P>
      </>
    ),
  },
  {
    id: "manuscript-sharing",
    q: "Should I share my manuscript through Last Bookmark?",
    a: (
      <>
        <P>
          Authors should only share manuscripts, documents, or other materials they are comfortable
          providing to a selected beta reader.
        </P>
        <P>
          Last Bookmark plans to offer optional NDA templates in the future. However, an NDA or
          other written agreement cannot completely guarantee that another person will keep shared
          materials confidential.
        </P>
        <P>
          Users should use good judgment and avoid publicly sharing confidential personal
          information or sensitive materials.
        </P>
      </>
    ),
  },
  {
    id: "reviews-quality",
    q: "What are reviews and quality scores?",
    a: (
      <>
        <P>
          Reviews and Quality Scores help users learn about another member&rsquo;s previous
          experiences on the platform.
        </P>
        <P>
          After a completed project, both the author and the beta reader can rate each other across five categories: communication, thoroughness, constructive feedback, professionalism, and on-time delivery.
        </P>
        <P>
          Beta readers also receive a Quality Score computed from their reviews and completion history, and they can apply for the Last Bookmark Certified badge from the Quality &amp; Certifications page. Certified readers show a badge on their profile and in search results.
        </P>
        <P>
          Reviews, scores, and badges provide helpful context, but they are not guarantees of a
          user&rsquo;s reliability, conduct, or future performance.
        </P>
      </>
    ),
  },
  {
    id: "report-inappropriate",
    q: "How do I report inappropriate behavior?",
    a: (
      <>
        <P>
          Use the available report or feedback tools to report harassment, scams, misleading
          projects, inappropriate messages, unsafe behavior, or violations of the Community
          Guidelines.
        </P>
        <P>
          You may also report a concern by emailing: <Email />
        </P>
        <P>
          Please include as much information as possible, including the user’s name, the
          project involved, what happened, and screenshots when available.
        </P>
      </>
    ),
  },
  {
    id: "block-user",
    q: "Can I block another user?",
    a: (
      <>
        <P>
          Yes. You can block another user from your Settings page under Blocked users. Blocked users can&rsquo;t send you messages or Coffee Chat requests, and they are not notified when you block them. Existing conversations stay visible so records aren&rsquo;t lost.
        </P>
        <P>
          You can also unblock a user at any time from the same Settings section.
        </P>
        <P>Serious misconduct, threats, harassment, scams, or safety concerns should always be reported to <Email /> in addition to blocking.</P>
      </>
    ),
  },
  {
    id: "report-bug",
    q: "How should I report a bug or confusing feature?",
    a: (
      <>
        <P>
          Use the Feedback button on the website to report bugs, broken links, errors, confusing
          instructions, or features that do not work as expected.
        </P>
        <P>
          You may also email <Email />.
        </P>
        <P>When possible, please include:</P>
        <ul className="list-disc pl-6 text-sm sm:text-base leading-relaxed text-foreground/90 space-y-1">
          <li>The page you were using</li>
          <li>What you clicked or attempted to do</li>
          <li>What you expected to happen</li>
          <li>What actually happened</li>
          <li>The type of phone, tablet, or computer you were using</li>
          <li>Your browser, if known</li>
          <li>A screenshot of the issue</li>
        </ul>
      </>
    ),
  },
  {
    id: "update-profile",
    q: "How do I update my profile?",
    a: (
      <>
        <P>
          Users can update their profile through their dashboard, profile area, or account settings.
        </P>
        <P>
          Depending on the selected role, editable information may include a profile photo, banner,
          biography, genres, reading preferences, experience, languages, availability, and other
          profile details.
        </P>
      </>
    ),
  },
  {
    id: "email-preferences",
    q: "How do I change my email preferences?",
    a: (
      <>
        <P>
          Available email preferences may be managed through the user’s account settings or by
          using the unsubscribe option included in eligible emails.
        </P>
        <P>
          Users who unsubscribe from optional updates may still receive necessary account, security,
          legal, or service-related communications.
        </P>
      </>
    ),
  },
  {
    id: "delete-account",
    q: "How do I delete my account?",
    a: (
      <>
        <P>A self-service account deletion option may not yet be available in account settings.</P>
        <P>
          To request account deletion, email <Email /> using the email address connected to your
          Last Bookmark account.
        </P>
        <P>
          Deleting an account may remove access to profiles, projects, applications, messages,
          reviews, and other account information. Certain records may be retained when reasonably
          necessary for legal compliance, fraud prevention, safety, dispute resolution, or
          enforcement of the Terms of Service.
        </P>
      </>
    ),
  },
  {
    id: "user-conduct",
    q: "Is Last Bookmark responsible for user conduct?",
    a: (
      <>
        <P>
          Last Bookmark provides a platform where authors and beta readers can connect, but it does
          not employ, supervise, or directly control its users.
        </P>
        <P>
          Users are responsible for their own conduct, communications, agreements, work, payment
          arrangements, and decisions.
        </P>
        <P>
          Last Bookmark reserves the right to warn, restrict, suspend, or permanently remove users
          who violate the Terms of Service or Community Guidelines, engage in unsafe or dishonest
          behavior, misuse the platform, or put other community members at risk.
        </P>
      </>
    ),
  },
  {
    id: "contact-us",
    q: "How do I contact Last Bookmark?",
    a: (
      <>
        <P>A dedicated Contact page may be added in a future update.</P>
        <P>
          For now, users can contact Last Bookmark by using the website’s Feedback button or
          emailing: <Email />
        </P>
        <P>
          This email may be used for account assistance, bug reports, safety concerns, policy
          questions, account-deletion requests, and general support.
        </P>
      </>
    ),
  },
];

export default function FAQ() {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ_ITEMS;
    // Search question text only — answer bodies contain JSX so we keep the
    // filter lightweight and predictable.
    return FAQ_ITEMS.filter((i) => i.q.toLowerCase().includes(q));
  }, [query]);

  return (
    <SiteShell>
      <section className="container-page py-12 sm:py-16" data-testid="faq-page">
        <div className="max-w-3xl">
          <span className="chip inline-flex items-center gap-1.5">
            <HelpCircle size={14} /> Help
          </span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight">
            Frequently Asked Questions
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
            Answers to the most common questions about how Last Bookmark works, what to expect
            during beta, and how we’re building the platform with you. Still stuck? Email{" "}
            <Email />.
          </p>

          <label className="relative block mt-8 max-w-md" data-testid="faq-search-wrap">
            <span className="sr-only">Search FAQ</span>
            <SearchIcon
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions…"
              className="w-full rounded-full border border-border bg-background pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-clay))]/40 transition"
              data-testid="faq-search-input"
            />
          </label>

          <div className="mt-8 max-w-2xl" data-testid="faq-platform-fee-notice">
            <PlatformFeeNotice variant="real" />
          </div>
        </div>

        <div className="mt-10 max-w-3xl">
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-border bg-muted/40 p-6 text-sm text-muted-foreground" data-testid="faq-empty">
              No questions match “{query}”. Try a different search term or email{" "}
              <Email />.
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {filtered.map((item) => (
                <AccordionItem
                  key={item.id}
                  value={item.id}
                  data-testid={`faq-item-${item.id}`}
                >
                  <AccordionTrigger
                    className="text-left font-serif text-lg sm:text-xl py-5 hover:no-underline"
                    data-testid={`faq-trigger-${item.id}`}
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="pt-1 pb-5">
                    <div
                      className="space-y-3 max-w-2xl"
                      data-testid={`faq-answer-${item.id}`}
                    >
                      {item.a}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>

        {/* Foot CTA — mirrors the tone of the legal-page footer without
            duplicating its sibling grid. Keeps the page self-contained. */}
        <div className="mt-16 max-w-3xl border-t pt-8">
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            Still need a hand?
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            We’re a small team and read every message. For account help, safety concerns,
            bug reports, or anything else, reach us at{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="inline-flex items-center gap-1 text-[hsl(var(--sb-clay))] underline underline-offset-4 hover:no-underline"
              data-testid="faq-footer-mailto"
            >
              <Mail size={14} /> {CONTACT_EMAIL}
            </a>
            .
          </p>
          <div className="mt-6 flex flex-wrap gap-2 text-sm">
            <Link
              to="/community-guidelines"
              className="rounded-full border px-3 py-1.5 hover:bg-muted/40 transition-colors"
              data-testid="faq-link-guidelines"
            >
              Community Guidelines
            </Link>
            <Link
              to="/terms"
              className="rounded-full border px-3 py-1.5 hover:bg-muted/40 transition-colors"
              data-testid="faq-link-terms"
            >
              Terms of Service
            </Link>
            <Link
              to="/privacy"
              className="rounded-full border px-3 py-1.5 hover:bg-muted/40 transition-colors"
              data-testid="faq-link-privacy"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
